import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClientPick } from '@/api/base';
import { api, ApiError } from '@/api/client';
import { useLeadSources, useM3Mutation, useReasons } from '@/api/m3';
import type { DealCard } from '@/api/m3-types';
import { TextAreaField, TextField } from '@/components/kit/Field';
import { SectionLabel } from '@/components/kit/Layout';
import { Modal } from '@/components/kit/Modal';
import { OptionList } from '@/components/kit/OptionList';
import { useToast } from '@/components/kit/Toast';
import { money } from '@/lib/format';
import { formatPhone } from '@/lib/phone';
import { useDebounced } from '@/features/base/shared/EntityPickers';
import { errorText } from '@/features/base/shared/useUndoable';
import { useOpenEvents } from '../leads/LeadDialogs';
import { useM3Submit } from '../useM3Submit';

/**
 * «＋ Сделка» (newDeal в макете, ТЗ 6.3): клиент, мероприятие, канал.
 * Под капотом — мини-заявка: сделок мимо заявки нет. Сделка на это
 * мероприятие уже есть — сервер отвечает 409 со ссылкой, открываем её.
 */
export function NewDealModal({ onClose, clientId: fixedClient }: { onClose: () => void; clientId?: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const sources = useLeadSources();
  const events = useOpenEvents();
  const [q, setQ] = useState('');
  const query = useDebounced(q, 250);
  const found = useClientPick(query);
  const [clientId, setClientId] = useState<string | null>(fixedClient ?? null);
  const [eventId, setEventId] = useState<string | null>(null);
  const [sourceId, setSourceId] = useState<string | null>(null);
  const create = useM3Mutation(() => api.post<{ id: string }>('/deals', { clientId, eventId, sourceId }));

  const submit = () =>
    create.mutate(undefined, {
      onSuccess: (r) => {
        toast.show('Сделка создана');
        onClose();
        navigate(`/crm/deals/${r.id}`);
      },
      onError: (e) => {
        // Одна сделка на клиента × мероприятие (БЛ 5.5): открываем существующую
        if (e instanceof ApiError && e.code === 'deal_exists' && typeof e.details.deal_id === 'string') {
          toast.show(e.message);
          onClose();
          navigate(`/crm/deals/${e.details.deal_id}`);
          return;
        }
        toast.showError(errorText(e));
      },
    });

  // Клиент без ФИО (заведён заявкой) сначала квалифицируется — в выборе его нет, как в макете
  const clients = (found.data ?? []).filter((c) => c.initials !== '#').slice(0, 6);

  return (
    <Modal
      title="Новая сделка"
      size="wide"
      onClose={onClose}
      confirm={{ label: 'Создать сделку', onClick: submit, disabled: !clientId || !eventId || !sourceId || create.isPending }}
    >
      <p>
        Сделка без входящей заявки — повторная продажа, договорились на мероприятии. Под капотом заведётся мини-заявка
        выбранного канала: сделок мимо заявки нет.
      </p>
      {fixedClient ? null : (
        <>
          <TextField label="Клиент" placeholder="ФИО или телефон" value={q} onValue={setQ} />
          <OptionList
            value={clientId}
            onChange={setClientId}
            options={clients.map((c) => ({
              value: c.id,
              title: c.fullName,
              description: [c.phone ? formatPhone(c.phone) : null, c.city?.name].filter(Boolean).join(' · '),
            }))}
          />
        </>
      )}
      <SectionLabel>Мероприятие</SectionLabel>
      <OptionList value={eventId} onChange={setEventId} options={events.map((e) => ({ value: e.id, title: e.label }))} />
      <SectionLabel>Канал</SectionLabel>
      <OptionList
        value={sourceId}
        onChange={setSourceId}
        options={(sources.data ?? [])
          .filter((s) => s.isActive && ['call', 'chat', 'offline'].includes(s.code))
          .map((s) => ({ value: s.id, title: s.name }))}
      />
    </Modal>
  );
}

/** Проигрыш (lost в макете, БЛ 5.9): причина обязательна */
export function LoseModal({ deal, onClose }: { deal: DealCard; onClose: () => void }) {
  const reasons = useReasons('deal');
  const [reasonId, setReasonId] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const { submit, pending } = useM3Submit(
    () => api.post(`/deals/${deal.id}/lose`, { reasonId, comment: comment.trim() || null }),
    'Сделка отмечена проигранной',
    onClose,
  );
  const open = deal.registrations.filter((r) => r.status === 'active' || r.status === 'reserve');
  if (open.length) {
    return (
      <Modal title="Отметить проигрыш нельзя" onClose={onClose}>
        <p>
          В сделке есть открытые регистрации:{' '}
          {open.map((r, i) => (
            <span key={r.id}>
              {i ? ', ' : ''}
              <b>{r.who}</b> · {r.status === 'active' ? 'участвует' : 'резерв'}
            </span>
          ))}
          .
        </p>
        <p>Сначала оформите по ним отказ или перенос — тогда сделку можно будет проиграть.</p>
      </Modal>
    );
  }
  return (
    <Modal
      title="Сделка проиграна"
      onClose={onClose}
      confirm={{ label: 'Отметить проигрыш', onClick: submit, disabled: !reasonId || pending, danger: true }}
    >
      <p>
        {deal.client.shortName} · {deal.event.label}. Причина обязательна.
      </p>
      <OptionList
        value={reasonId}
        onChange={setReasonId}
        options={(reasons.data ?? []).filter((r) => r.isActive).map((r) => ({ value: r.id, title: r.name }))}
      />
      <TextAreaField label="Комментарий" value={comment} onValue={setComment} />
    </Modal>
  );
}

/** Прогноз (plan в макете): сумма, которую ждёт менеджер, пока номеров нет */
export function ForecastModal({ deal, onClose }: { deal: DealCard; onClose: () => void }) {
  const [value, setValue] = useState(deal.forecast ? money(deal.forecast) : '');
  const amount = value.trim() ? Number(value.replace(/[^\d,.]/g, '').replace(',', '.')) : null;
  const { submit, pending } = useM3Submit(
    () => api.patch(`/deals/${deal.id}`, { forecast: amount }),
    'Прогноз сохранён',
    onClose,
  );
  return (
    <Modal
      title="Прогноз по сделке"
      onClose={onClose}
      confirm={{ label: 'Сохранить', onClick: submit, disabled: pending || (amount !== null && Number.isNaN(amount)) }}
    >
      <p>Прогноз вводит менеджер, пока номеров нет. План считается по зарегистрированным номерам, факт — по платежам.</p>
      <TextField label="Прогноз" inputMode="numeric" placeholder="например, 45 000" value={value} onValue={setValue} />
    </Modal>
  );
}
